import * as React from 'react';
import { useEffect, useState, useContext } from 'react';
import ApiClient from '../../Services/ApiClient';
import { useAlert } from '../ui/alertProvider';
import AuthContext from '../../store/authContext';
import ConfirmModal from '../ui/confirmModal';
import ClinicSettingsForm from './clinicSettings';

const SiteSettingsManager: React.FC = () => {
  const [sites, setSites] = useState<any[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'settings' | 'clinic'>('settings');
  const [linkedSettings, setLinkedSettings] = useState<any[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState<any | null>(null);
  // Footer fields from strongly-typed settings
  const [footerSettings, setFooterSettings] = useState<any>(null);
  // WhatsApp specific settings
  const [whatsAppEnabled, setWhatsAppEnabled] = useState<boolean>(false);
  const [whatsAppToken, setWhatsAppToken] = useState<string>('');
  const [whatsAppPhone, setWhatsAppPhone] = useState<string>('');
  const [whatsAppTemplate, setWhatsAppTemplate] = useState<string>('');
  const [templateValidation, setTemplateValidation] = useState<{ hasName: boolean; hasMeeting: boolean }>({ hasName: false, hasMeeting: false });
  const { showAlert } = useAlert();
  const auth = useContext(AuthContext) as any;

  // Determine who may edit WhatsApp settings: SuperAdmin, Admin, or a specific role 'WhatsAppManager'
  const canEditWhatsApp = !!(auth?.isSuperAdmin || auth?.isAdmin || (auth?.roles && Array.isArray(auth.roles) && auth.roles.includes('WhatsAppManager')));

  // Quick client-side validation helper for WhatsApp phone id
  const whatsAppPhoneLooksNumeric = (val: string) => !!val && /^[0-9]+$/.test(val);
  const whatsAppPhoneLooksLikeLocalNumber = (val: string) => !!val && /^0\d+/.test(val);

  useEffect(() => {
    const load = async () => {
      try {
        const res: any = await ApiClient.get('/api/sites');
        setSites(res || []);
      } catch (e) {
        console.error(e);
        showAlert('Failed to load sites', 'error');
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedSiteId) return;
    const load = async () => {
      setLoading(true);
      try {
        const res: any = await ApiClient.get(`/api/sites/${selectedSiteId}/settings`);
        setLinkedSettings(res || []);
        // Also load strongly-typed settings for footer fields
        const strong: any = await ApiClient.get(`/api/settings/site/${selectedSiteId}/settings`);
        setFooterSettings(strong || null);
        // load WhatsApp-specific settings from site settings
        if (strong) {
          setWhatsAppEnabled(!!strong.whatsAppEnabled || !!strong.WhatsAppEnabled || false);
          setWhatsAppToken(strong.whatsAppApiToken || strong.WhatsAppApiToken || '');
          setWhatsAppPhone(strong.whatsAppPhoneNumber || strong.WhatsAppPhoneNumber || '');
          // load new interval settings
          // prefer camelCase names returned by API
          const leadsInterval = strong.leadsSummaryIntervalMinutes ?? strong.LeadsSummaryIntervalMinutes ?? null;
          const trafficInterval = strong.trafficSummaryIntervalMinutes ?? strong.TrafficSummaryIntervalMinutes ?? null;
          setLeadsInterval(leadsInterval ?? 1440);
          setTrafficInterval(trafficInterval ?? 1440);
          const tmpl = strong.whatsAppPaymentMessage || strong.WhatsAppPaymentMessage || '';
          setWhatsAppTemplate(tmpl);
          setTemplateValidation({ hasName: tmpl.includes('{Name}'), hasMeeting: tmpl.includes('{MeetingId}') });
        }
      } catch (e) {
        console.error(e);
        showAlert('Failed to load site settings', 'error');
      } finally { setLoading(false); }
    };
    load();
  }, [selectedSiteId]);

  const startEdit = (s: any) => {
    setEditingKey(s.settingKey || s.settingKey);
    setEditingValue(s.settingValue ?? '');
  };

  // new UI state for editing intervals
  const [leadsInterval, setLeadsInterval] = useState<number>(1440);
  const [trafficInterval, setTrafficInterval] = useState<number>(1440);

  const saveEdit = async () => {
    if (!selectedSiteId || !editingKey) return;
    setLoading(true);
    try {
      const body = { SettingKey: editingKey, SettingValue: editingValue, DataType: 'String', Description: '' };
      await ApiClient.put(`/api/settings/site/${selectedSiteId}/${encodeURIComponent(editingKey)}`, body);
        // Audit the edit via server-side audit endpoint (if available) by creating an audit log entry
        try {
          await ApiClient.post('/api/audit/logs', { Action: 'SiteSettingEdited', Target: editingKey, Details: `Updated value to ${editingValue}`, SiteId: selectedSiteId });
        } catch { /* ignore audit failures */ }
      showAlert('Setting updated', 'success');
      const res: any = await ApiClient.get(`/api/settings/site/${selectedSiteId}`);
      setLinkedSettings(res || []);
      setEditingKey(null);
      setEditingValue('');
    } catch (e: any) {
      console.error(e);
      showAlert(e?.data || 'Failed to update setting', 'error');
    } finally { setLoading(false); }
  };

  const saveIntervals = async () => {
    if (!selectedSiteId) return;
    setLoading(true);
    try {
      // Save as GlobalSettings linked to the site
      const leadBody = { SettingKey: 'LeadsSummaryIntervalMinutes', SettingValue: String(leadsInterval), DataType: 'Int', Description: 'Interval minutes for leads digest' };
      const trafficBody = { SettingKey: 'TrafficSummaryIntervalMinutes', SettingValue: String(trafficInterval), DataType: 'Int', Description: 'Interval minutes for traffic digest' };
      await ApiClient.put(`/api/settings/site/${selectedSiteId}/${encodeURIComponent('LeadsSummaryIntervalMinutes')}`, leadBody);
      await ApiClient.put(`/api/settings/site/${selectedSiteId}/${encodeURIComponent('TrafficSummaryIntervalMinutes')}`, trafficBody);
      showAlert('Intervals updated', 'success');
      const strong: any = await ApiClient.get(`/api/settings/site/${selectedSiteId}/settings`);
      setFooterSettings(strong || null);
    } catch (e: any) {
      console.error(e);
      showAlert('Failed to save intervals', 'error');
    } finally { setLoading(false); }
  };

  const unlink = async (key: string) => {
    if (!selectedSiteId) return;
    setConfirmKey(key);
    setConfirmOpen(true);
  };

  const confirmUnlink = async () => {
    if (!selectedSiteId || !confirmKey) return;
    setConfirmLoading(true);
      try {
      await ApiClient.delete(`/api/sites/${selectedSiteId}/settings/${encodeURIComponent(confirmKey)}`);
      try { await ApiClient.post('/api/audit/logs', { Action: 'SiteSettingUnlinked', Target: confirmKey, Details: '', SiteId: selectedSiteId }); } catch {}
      showAlert('Setting unlinked from site', 'success');
      const res: any = await ApiClient.get(`/api/settings/site/${selectedSiteId}`);
      setLinkedSettings(res || []);
    } catch (e: any) {
      console.error(e);
      showAlert(e?.data || 'Failed to unlink setting', 'error');
    } finally { setConfirmLoading(false); setConfirmOpen(false); setConfirmKey(null); }
  };

  if (!auth?.isLoggedIn) {
    return <div className="auth-warning">Only authenticated users can access this page. Please sign in.</div>;
  }

 return (
    <div className="page-padding">
      <h2>Site Settings Manager (SuperAdmin)</h2>
      <div className="mb-12">
        <label>Select site: </label>
        <select value={selectedSiteId ?? ''} onChange={e => setSelectedSiteId(e.target.value ? Number(e.target.value) : null)}>
          <option value=''>-- select site --</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.siteName ?? s.domain} ({s.domain})</option>)}
        </select>
      </div>

      {selectedSiteId && (
        <div>
          {/* Tab Navigation */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '2px solid #ddd' }}>
            <button 
              onClick={() => setActiveTab('settings')}
              style={{
                padding: '8px 16px',
                background: activeTab === 'settings' ? '#007bff' : 'transparent',
                color: activeTab === 'settings' ? 'white' : 'black',
                border: 'none',
                cursor: 'pointer',
                fontWeight: activeTab === 'settings' ? 'bold' : 'normal'
              }}
            >
              Settings
            </button>
            <button 
              onClick={() => setActiveTab('clinic')}
              style={{
                padding: '8px 16px',
                background: activeTab === 'clinic' ? '#007bff' : 'transparent',
                color: activeTab === 'clinic' ? 'white' : 'black',
                border: 'none',
                cursor: 'pointer',
                fontWeight: activeTab === 'clinic' ? 'bold' : 'normal'
              }}
            >
              Clinic Settings
            </button>
          </div>

          {/* Clinic Settings Tab */}
          {activeTab === 'clinic' && (
            <div style={{ marginBottom: '24px' }}>
              <ClinicSettingsForm siteId={selectedSiteId} />
            </div>
          )}

          {/* Settings Tab Content */}
          {activeTab === 'settings' && (
            <div>
          <div style={{ margin: '12px 0', padding: '8px', background: '#f8f9fa', borderRadius: 6 }}>
            <button className="btn" style={{ marginRight: 8 }}
              onClick={async () => {
                try {
                  await ApiClient.post(`/traffic/admin/send-digest/${selectedSiteId}`);
                  showAlert('Traffic digest triggered', 'success');
                } catch (e: any) {
                  showAlert(e?.data || 'Failed to trigger traffic digest', 'error');
                }
              }}>
              Send Traffic Digest
            </button>
            <button className="btn"
              onClick={async () => {
                try {
                  await ApiClient.post(`/leads/send-digest/${selectedSiteId}`);
                  showAlert('Leads digest triggered', 'success');
                } catch (e: any) {
                  showAlert(e?.data || 'Failed to trigger leads digest', 'error');
                }
              }}>
              Send Leads Digest
            </button>
          </div>
          <h3>Linked site settings</h3>
          {loading ? <div>Loading...</div> : (
            <>
              <div style={{ margin: '12px 0' }}>
                <h4>Digest intervals (minutes)</h4>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <label>Leads digest interval:</label>
                  <input type="number" value={leadsInterval} onChange={e => setLeadsInterval(Number(e.target.value || 0))} min={1} />
                  <label>Traffic digest interval:</label>
                  <input type="number" value={trafficInterval} onChange={e => setTrafficInterval(Number(e.target.value || 0))} min={1} />
                  <button className="btn" onClick={saveIntervals}>Save intervals</button>
                </div>
              </div>
              <div className="settings-list">
                <div className="setting-header">
                  <div className="setting-key">Key</div>
                  <div className="setting-value">Value</div>
                  <div className="setting-actions text-right">Actions</div>
                </div>
                {linkedSettings
                  .map((s: any) => {
                    const dataType = (s.dataType || s.DataType || '').toString();
                    const isBoolean = dataType.toLowerCase() === 'boolean' || (s.settingValue === 'true' || s.settingValue === 'false');
                    return (
                      <div key={s.id} className="setting-row">
                        <div className="setting-key">{s.settingKey}</div>
                        <div className="setting-value">
                          {editingKey === s.settingKey ? (
                            isBoolean ? (
                              <input type="checkbox" checked={editingValue === 'true'} onChange={e => setEditingValue(e.target.checked ? 'true' : 'false')} />
                            ) : (
                              <textarea className="textarea-full" value={editingValue} onChange={e => setEditingValue(e.target.value)} />
                            )
                          ) : (
                            isBoolean ? (
                              <input type="checkbox" checked={String(s.settingValue).toLowerCase() === 'true'} readOnly />
                            ) : (
                              <textarea readOnly className="textarea-full" value={s.settingValue} title={s.settingValue} />
                            )
                          )}
                        </div>
                        <div className="setting-actions text-right">
                          {editingKey === s.settingKey ? (
                            <>
                              <button onClick={saveEdit} disabled={loading} className="btn">Save</button>
                              <button onClick={() => { setEditingKey(null); setEditingValue(''); }} className="btn ml-8">Cancel</button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => setSelectedSetting(s)} className="btn">פרטים</button>
                              <button onClick={() => startEdit(s)} className="btn ml-8">Edit</button>
                              <button onClick={() => unlink(s.settingKey)} className="btn ml-8">Unlink</button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </>
          )}
              </div>
            )}

          </div>
        )}
        {/* Details modal for a selected setting (mobile) */}
      {selectedSetting && (
        <div className="modal-overlay" onClick={() => setSelectedSetting(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedSetting(null)}>×</button>
            <h3>Setting: {selectedSetting.settingKey}</h3>
            <div style={{ marginTop: 8 }}>
              <div><strong>Value:</strong></div>
              <pre style={{ whiteSpace: 'pre-wrap' }}>{selectedSetting.settingValue}</pre>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn" onClick={() => { setSelectedSetting(null); setEditingKey(selectedSetting.settingKey); setEditingValue(String(selectedSetting.settingValue)); }}>Edit</button>
              <button className="btn ml-8" onClick={() => { setSelectedSetting(null); unlink(selectedSetting.settingKey); }}>Unlink</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal open={confirmOpen} title="Unlink setting" message={<>Are you sure you want to unlink <strong>{confirmKey}</strong>?</>} loading={confirmLoading} onConfirm={confirmUnlink} onCancel={() => { setConfirmOpen(false); setConfirmKey(null); }} />
    </div>
  );
};

export default SiteSettingsManager;


